import { Button, Card, Col, Form, Input, Row, Select, Space, theme, TreeSelect, DatePicker, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { computeButtonColLayout, filterFormGridLayout, filterFormItemLayout, filterFormItemStyle } from '@utils/property';
import { filterTreeNodeByTitle } from '@/utils/antdTreeSelect';
import { expertLevels } from '@utils/constants';
import './GuestFilterPanel.less';

const VISIBLE_FIELD_COUNT = 7;

const guestLaborDemandList = [
    { value: 0, label: '待定' },
    { value: 1, label: '需要' },
    { value: 2, label: '不需要' },
];

const guestIdentityList = [
    { value: 1, label: '参会嘉宾' },
    { value: 2, label: '参会代表' },
    { value: 3, label: '企业人员' },
    { value: 4, label: '工作人员' },
    { value: 0, label: '待定' },
];

const HAS_REMARKS_OPTIONS = [
    { value: 1, label: '有嘉宾备注' },
    { value: 0, label: '无嘉宾备注' },
];

export default function GuestFilterPanel({ filter, conferenceHallTreeData, dateOptions = [], collapsed = true, onFinish, onReset, onToggleCollapsed }) {
    const { token } = theme.useToken();
    const [form] = Form.useForm();
    const [hasTask, setHasTask] = useState(filter.hasTask);
    const hasInvitationOrg = Form.useWatch('hasInvitationOrg', form);
    const hasRemarks = Form.useWatch('hasRemarks', form);
    const expandedFieldCount = 7 + (hasTask === 1 ? 5 : 0) + 1 + 1 + 1 + (hasInvitationOrg !== 0 ? 1 : 0) + 1 + (hasRemarks !== 0 ? 1 : 0);
    const buttonColLayout = computeButtonColLayout(!collapsed ? expandedFieldCount : VISIBLE_FIELD_COUNT);

    useEffect(() => {
        form.setFieldsValue({
            name: filter.name,
            unitName: filter.unitName,
            unitOffices: filter.unitOffices,
            unitPost: filter.unitPost,
            roles: filter.roles,
            expertLevels: filter.expertLevels,
            hasTask: filter.hasTask,
            invitationAccepted: filter.invitationAccepted,
            agendTime: filter.agendTime,
            conferenceHallId: filter.conferenceHallId,
            taskBeginTime: filter.taskBeginTime,
            taskEndTime: filter.taskEndTime,
            syncedReg: filter.syncedReg,
            laborFeeDemand: filter.laborFeeDemand,
            hasInvitationOrg: filter.hasInvitationOrg,
            invitationOrgKeywords: filter.invitationOrgKeywords,
            hasRemarks: filter.hasRemarks,
            remarksKeyword: filter.remarksKeyword,
        });
        if (filter.hasTask !== undefined) {
            setHasTask(filter.hasTask);
        }
    }, [filter, form]);

    const handleReset = () => {
        setHasTask(undefined);
        onReset();
    };

    const handleHasTaskChange = value => {
        setHasTask(value);
        if (value !== 1) {
            form.setFieldsValue({
                invitationAccepted: undefined,
                agendTime: undefined,
                conferenceHallId: undefined,
                taskBeginTime: undefined,
                taskEndTime: undefined,
            });
        }
    };

    return (
        <Card
            size="small"
            className="guest-filter-panel"
            style={{
                marginBottom: token.marginLG,
                border: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorFillAlter,
            }}
        >
            <Form {...filterFormItemLayout} form={form} onFinish={onFinish} autoComplete="off" requiredMark={false}>
                <Row gutter={[16, 16]}>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="嘉宾姓名" name="name" style={filterFormItemStyle}>
                            <Input allowClear placeholder="嘉宾姓名" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="单位" name="unitName" style={filterFormItemStyle}>
                            <Input allowClear placeholder="单位名称关键字" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="科室/部门" name="unitOffices" style={filterFormItemStyle}>
                            <Input allowClear placeholder="所在科室或部门关键字" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="职务" name="unitPost" style={filterFormItemStyle}>
                            <Input allowClear placeholder="职务关键字" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="参会人员类别" name="roles" style={filterFormItemStyle}>
                            <Select mode="multiple" maxTagCount="responsive" options={guestIdentityList} placeholder="请选择身份" allowClear />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="嘉宾级别" name="expertLevels" style={filterFormItemStyle}>
                            <Select mode="multiple" maxTagCount="responsive" options={expertLevels} fieldNames={{ label: 'label', value: 'level' }} placeholder="请选择嘉宾级别" allowClear />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="有无会议任务" name="hasTask" style={filterFormItemStyle}>
                            <Select
                                options={[
                                    { value: 1, label: '有会议任务' },
                                    { value: 0, label: '无会议任务' },
                                ]}
                                placeholder="选择有无会议任务"
                                allowClear
                                onChange={handleHasTaskChange}
                            />
                        </Form.Item>
                    </Col>

                    {!collapsed && (
                        <>
                            {hasTask === 1 && (
                                <>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="嘉宾任务状态" name="invitationAccepted" style={filterFormItemStyle}>
                                            <Select
                                                options={[
                                                    { value: 0, label: '待确认' },
                                                    { value: 1, label: '已确认（含部分确认）' },
                                                    { value: 2, label: '已拒绝' },
                                                ]}
                                                placeholder="请选择嘉宾任务接受状态"
                                                allowClear
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col {...filterFormGridLayout}>
                                        <Form.Item
                                            label={
                                                <span className="form-item-label-text">
                                                    任务日期
                                                    <Tooltip
                                                        title={
                                                            <span>
                                                                任务开始时间 &lt; 12:00 为上午
                                                                <br />
                                                                任务开始时间 ≧ 12:00 为下午
                                                            </span>
                                                        }
                                                    >
                                                        <i className="bi bi-question-circle-fill" style={{ marginLeft: 4, color: '#faad14' }}></i>
                                                    </Tooltip>
                                                </span>
                                            }
                                            name="agendTime"
                                            style={filterFormItemStyle}
                                        >
                                            <Select options={dateOptions} placeholder="请选择日期" allowClear />
                                        </Form.Item>
                                    </Col>

                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="任务所属会议" name="conferenceHallId" style={filterFormItemStyle}>
                                            <TreeSelect showSearch={{ filterTreeNode: filterTreeNodeByTitle }} styles={{ popup: { root: { minWidth: 500, overflow: 'auto' } } }} allowClear treeDefaultExpandAll placement="bottomRight" treeData={conferenceHallTreeData} placeholder="请选择会议" />
                                        </Form.Item>
                                    </Col>

                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="任务开始时间" name="taskBeginTime" style={filterFormItemStyle}>
                                            <DatePicker.RangePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
                                        </Form.Item>
                                    </Col>

                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="任务结束时间" name="taskEndTime" style={filterFormItemStyle}>
                                            <DatePicker.RangePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
                                        </Form.Item>
                                    </Col>
                                </>
                            )}

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="同步至报名" name="syncedReg" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 0, label: '未同步' },
                                            { value: 1, label: '已同步' },
                                        ]}
                                        placeholder="请选择同步状态"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="有无邀请方" name="hasInvitationOrg" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '有邀请方' },
                                            { value: 2, label: '独家邀请方' },
                                            { value: 0, label: '无邀请方' },
                                        ]}
                                        placeholder="请选择有无邀请方"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.hasInvitationOrg !== cur.hasInvitationOrg}>
                                {({ getFieldValue }) =>
                                    getFieldValue('hasInvitationOrg') !== 0 ? (
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item label="邀请方" name="invitationOrgKeywords" style={filterFormItemStyle} preserve={false}>
                                                <Input allowClear placeholder="请输入邀请方关键字" />
                                            </Form.Item>
                                        </Col>
                                    ) : null
                                }
                            </Form.Item>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="有无嘉宾备注" name="hasRemarks" style={filterFormItemStyle}>
                                    <Select options={HAS_REMARKS_OPTIONS} placeholder="请选择有无嘉宾备注" allowClear />
                                </Form.Item>
                            </Col>

                            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.hasRemarks !== cur.hasRemarks}>
                                {({ getFieldValue }) =>
                                    getFieldValue('hasRemarks') !== 0 ? (
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item label="嘉宾备注" name="remarksKeyword" style={filterFormItemStyle} preserve={false}>
                                                <Input allowClear placeholder="嘉宾备注关键字" />
                                            </Form.Item>
                                        </Col>
                                    ) : null
                                }
                            </Form.Item>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="劳务需求" name="laborFeeDemand" style={filterFormItemStyle}>
                                    <Select options={guestLaborDemandList} placeholder="请选择劳务需求" allowClear />
                                </Form.Item>
                            </Col>
                        </>
                    )}

                    <Col {...buttonColLayout}>
                        <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                            <Space size="small">
                                <Button type="primary" htmlType="submit" icon={<i className="bi bi-search" aria-hidden="true" />}>
                                    检索
                                </Button>
                                <Button type="default" onClick={handleReset}>
                                    重置
                                </Button>
                                <Button type="link" size="small" aria-expanded={!collapsed} onClick={onToggleCollapsed} style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                    {!collapsed ? (
                                        <>
                                            收起 <span className="bi bi-chevron-up" aria-hidden="true" />
                                        </>
                                    ) : (
                                        <>
                                            展开 <span className="bi bi-chevron-down" aria-hidden="true" />
                                        </>
                                    )}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}
